import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchJson } from './adminApi';

// Browse any table in myDb.db: pick columns, filter on one column, sort and page (/admin/db/*).

const DB_TABLES_ENDPOINT = '/admin/db/tables';
const DB_TABLE_COLUMNS_ENDPOINT = '/admin/db/table/columns';
const DB_TABLE_QUERY_ENDPOINT = '/admin/db/table/query';
const DB_PAGE_SIZE = 50;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const toOptionalNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const formatInteger = (value) => new Intl.NumberFormat('sv-SE', {
    maximumFractionDigits: 0,
}).format(toNumber(value));

const pickFirst = (source, keys) => {
    for (const key of keys) {
        if (source && source[key] != null) {
            return source[key];
        }
    }
    return null;
};

const extractArrayLike = (payload, keys) => {
    if (Array.isArray(payload)) return payload;
    for (const key of keys) {
        const candidate = payload?.[key];
        if (Array.isArray(candidate)) return candidate;
    }
    return [];
};

const normalizeDbColumnMeta = (column) => {
    if (!column) return null;
    if (typeof column === 'string') {
        const normalizedName = column.trim();
        if (!normalizedName) return null;
        return {
            name: normalizedName,
            dataType: '',
            nullable: null,
            indexed: null,
        };
    }
    if (typeof column !== 'object') return null;

    const nameValue = pickFirst(column, ['name', 'column', 'columnName', 'column_name']);
    const normalizedName = String(nameValue || '').trim();
    if (!normalizedName) return null;

    const nullableValue = pickFirst(column, ['nullable', 'is_nullable', 'isNullable']);
    const indexedValue = pickFirst(column, ['indexed', 'is_indexed', 'isIndexed']);

    return {
        name: normalizedName,
        dataType: String(pickFirst(column, ['dataType', 'data_type', 'type']) || '').trim(),
        nullable: nullableValue == null ? null : Boolean(nullableValue),
        indexed: indexedValue == null ? null : Boolean(indexedValue),
    };
};

const normalizeDbTables = (payload) => {
    const sourceTables = extractArrayLike(payload, ['tables', 'data', 'items']);

    return sourceTables
        .map((tableEntry) => {
            if (!tableEntry || typeof tableEntry !== 'object') return null;

            const tableName = String(
                pickFirst(tableEntry, ['name', 'table', 'tableName', 'table_name']) || '',
            ).trim();
            if (!tableName) return null;

            const schema = String(pickFirst(tableEntry, ['schema', 'tableSchema', 'table_schema']) || 'public').trim() || 'public';
            const columns = extractArrayLike(tableEntry, ['columns', 'fields'])
                .map(normalizeDbColumnMeta)
                .filter(Boolean);
            const rowCountEstimate = toOptionalNumber(
                pickFirst(tableEntry, ['rowCountEstimate', 'row_count_estimate', 'estimated_rows', 'rowCount', 'row_count']),
            );
            const key = `${schema}.${tableName}`;

            return {
                key,
                schema,
                table: tableName,
                label: schema === 'public' ? tableName : `${schema}.${tableName}`,
                columns,
                rowCountEstimate,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
};

const normalizeDbTableColumns = (payload) => {
    const columns = extractArrayLike(payload, ['columns', 'fields'])
        .map(normalizeDbColumnMeta)
        .filter(Boolean);
    const primaryKey = extractArrayLike(payload, ['primary_key', 'primaryKey'])
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    return { columns, primaryKey };
};

const normalizeDbTableData = (payload, fallback = {}) => {
    const columnsRaw = extractArrayLike(payload, ['columns', 'fields']);
    const rowsRaw = extractArrayLike(payload, ['rows', 'data', 'items']);
    const normalizedColumnsFromPayload = columnsRaw
        .map(normalizeDbColumnMeta)
        .filter(Boolean);

    const inferredColumns = normalizedColumnsFromPayload.length === 0 && rowsRaw.length > 0 && rowsRaw[0] && typeof rowsRaw[0] === 'object'
        ? Object.keys(rowsRaw[0]).map((name) => ({
            name,
            dataType: '',
            nullable: null,
            indexed: null,
        }))
        : [];
    const normalizedColumns = normalizedColumnsFromPayload.length > 0 ? normalizedColumnsFromPayload : inferredColumns;

    const rows = rowsRaw
        .map((row) => (row && typeof row === 'object' ? row : null))
        .filter(Boolean);

    const page = Math.max(1, toNumber(pickFirst(payload, ['page']) || fallback.page || 1));
    const pageSize = Math.max(1, toNumber(pickFirst(payload, ['pageSize', 'page_size']) || fallback.pageSize || DB_PAGE_SIZE));
    const totalRows = Math.max(0, toNumber(pickFirst(payload, ['totalRows', 'total_rows', 'count', 'total']) || rows.length));
    const totalPages = Math.max(
        1,
        toNumber(pickFirst(payload, ['totalPages', 'total_pages']) || Math.ceil(totalRows / pageSize) || 1),
    );

    return {
        columns: normalizedColumns,
        rows,
        page,
        pageSize,
        totalRows,
        totalPages,
    };
};

const buildDbQueryUrl = (baseUrl, {
    schema,
    table,
    column,
    page = 1,
    pageSize = DB_PAGE_SIZE,
}) => {
    const query = new URLSearchParams();
    query.set('schema', String(schema || 'public'));
    query.set('table', String(table || ''));
    if (column) query.set('column', String(column));
    query.set('page', String(page));
    query.set('pageSize', String(pageSize));
    return `${baseUrl}?${query.toString()}`;
};

const formatCellValue = (value) => {
    if (value == null) return '-';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
        return JSON.stringify(value);
    } catch (error) {
        return String(value);
    }
};

const DatabaseTab = () => {
    const [dbTables, setDbTables] = useState([]);
    const [dbSelectedTableKey, setDbSelectedTableKey] = useState('');
    const [dbFilterColumn, setDbFilterColumn] = useState('');
    const [dbFilterValue, setDbFilterValue] = useState('');
    const [dbSortBy, setDbSortBy] = useState('');
    const [dbSortDir, setDbSortDir] = useState('desc');
    const [dbPage, setDbPage] = useState(1);
    const [dbPageSize, setDbPageSize] = useState(DB_PAGE_SIZE);
    const [dbColumns, setDbColumns] = useState([]);
    const [dbVisibleColumns, setDbVisibleColumns] = useState([]);
    const [dbRows, setDbRows] = useState([]);
    const [dbTotalRows, setDbTotalRows] = useState(0);
    const [dbTotalPages, setDbTotalPages] = useState(1);
    const [dbHasLoadedRows, setDbHasLoadedRows] = useState(false);
    const [isDbTablesLoading, setIsDbTablesLoading] = useState(false);
    const [isDbColumnsLoading, setIsDbColumnsLoading] = useState(false);
    const [isDbRowsLoading, setIsDbRowsLoading] = useState(false);
    const [dbTablesError, setDbTablesError] = useState('');
    const [dbColumnsError, setDbColumnsError] = useState('');
    const [dbRowsError, setDbRowsError] = useState('');

    const handleLoadDbTables = useCallback(async () => {
        setIsDbTablesLoading(true);
        setDbTablesError('');

        try {
            const payload = await fetchJson(DB_TABLES_ENDPOINT);
            const normalizedTables = normalizeDbTables(payload);
            setDbTables(normalizedTables);
            setDbSelectedTableKey((previousKey) => {
                if (previousKey && normalizedTables.some((entry) => entry.key === previousKey)) {
                    return previousKey;
                }
                return normalizedTables[0]?.key || '';
            });
            if (normalizedTables.length === 0) {
                setDbColumns([]);
                setDbVisibleColumns([]);
                setDbFilterColumn('');
                setDbFilterValue('');
                setDbRows([]);
                setDbTotalRows(0);
                setDbTotalPages(1);
                setDbHasLoadedRows(false);
            }
        } catch (error) {
            setDbTablesError(error.message || 'Could not load database tables.');
        } finally {
            setIsDbTablesLoading(false);
        }
    }, []);

    const handleLoadDbColumns = useCallback(async () => {
        const selectedTable = dbTables.find((entry) => entry.key === dbSelectedTableKey);
        if (!selectedTable) {
            setDbColumnsError('Choose a table first.');
            return;
        }

        const requestUrl = buildDbQueryUrl(DB_TABLE_COLUMNS_ENDPOINT, {
            schema: selectedTable.schema,
            table: selectedTable.table,
        });

        setIsDbColumnsLoading(true);
        setDbColumnsError('');

        try {
            const payload = await fetchJson(requestUrl);
            const normalizedData = normalizeDbTableColumns(payload);
            const filterableColumns = normalizedData.columns.filter(
                (column) => String(column.name || '').toLowerCase() !== 'all',
            );
            setDbColumns(filterableColumns);
            setDbVisibleColumns((previous) => {
                const nextColumnNames = filterableColumns.map((column) => column.name);
                if (nextColumnNames.length === 0) return [];
                if (previous.length === 0) return nextColumnNames;
                const allowedNames = new Set(nextColumnNames);
                const keptNames = previous.filter((name) => allowedNames.has(name));
                return keptNames.length > 0 ? keptNames : nextColumnNames;
            });
            setDbFilterColumn((previous) => {
                if (
                    previous
                    && previous.toLowerCase() !== 'all'
                    && filterableColumns.some((column) => column.name === previous)
                ) {
                    return previous;
                }
                return filterableColumns[0]?.name || '';
            });
        } catch (error) {
            setDbColumnsError(error.message || 'Could not load table columns.');
        } finally {
            setIsDbColumnsLoading(false);
        }
    }, [
        dbSelectedTableKey,
        dbTables,
    ]);

    const handleLoadDbRows = useCallback(async (options = {}) => {
        const selectedTable = dbTables.find((entry) => entry.key === dbSelectedTableKey);
        if (!selectedTable) {
            setDbRowsError('Choose a table first.');
            return;
        }

        const requestedPage = Math.max(1, toNumber(options.page ?? dbPage));
        const requestedSortBy = options.sortBy ?? dbSortBy;
        const requestedSortDir = options.sortDir ?? dbSortDir;
        const selectedColumns = (dbVisibleColumns.length > 0
            ? dbVisibleColumns
            : dbColumns.map((column) => column.name))
            .filter((name) => String(name || '').toLowerCase() !== 'all');
        const filters = [];
        if (dbFilterColumn && dbFilterValue !== '') {
            filters.push({
                column: dbFilterColumn,
                op: 'eq',
                value: dbFilterValue,
            });
        }

        setIsDbRowsLoading(true);
        setDbRowsError('');

        try {
            const payload = await fetchJson(DB_TABLE_QUERY_ENDPOINT, {
                method: 'POST',
                body: JSON.stringify({
                    schema: selectedTable.schema,
                    table: selectedTable.table,
                    page: requestedPage,
                    pageSize: dbPageSize,
                    sort: requestedSortBy
                        ? { column: requestedSortBy, dir: requestedSortDir }
                        : null,
                    filters,
                    selectedColumns,
                }),
            });
            const normalizedData = normalizeDbTableData(payload, {
                page: requestedPage,
                pageSize: dbPageSize,
            });

            if (normalizedData.columns.length > 0) {
                const filterableColumns = normalizedData.columns.filter(
                    (column) => String(column.name || '').toLowerCase() !== 'all',
                );
                setDbColumns(filterableColumns);
            }
            setDbRows(normalizedData.rows);
            setDbPage(normalizedData.page);
            setDbPageSize(normalizedData.pageSize);
            setDbTotalRows(normalizedData.totalRows);
            setDbTotalPages(normalizedData.totalPages);
            setDbSortBy(requestedSortBy);
            setDbSortDir(requestedSortDir);
            setDbHasLoadedRows(true);
        } catch (error) {
            setDbRowsError(error.message || 'Could not load table rows.');
        } finally {
            setIsDbRowsLoading(false);
        }
    }, [
        dbColumns,
        dbFilterColumn,
        dbFilterValue,
        dbPage,
        dbPageSize,
        dbSelectedTableKey,
        dbSortBy,
        dbSortDir,
        dbTables,
        dbVisibleColumns,
    ]);

    useEffect(() => {
        if (!dbSelectedTableKey) {
            setDbColumns([]);
            setDbVisibleColumns([]);
            setDbFilterColumn('');
            setDbFilterValue('');
            setDbColumnsError('');
            setDbRows([]);
            setDbTotalRows(0);
            setDbTotalPages(1);
            setDbPage(1);
            setDbHasLoadedRows(false);
            setDbRowsError('');
            setDbSortBy('');
            setDbSortDir('desc');
            return;
        }

        void handleLoadDbColumns();
        setDbFilterValue('');
        setDbRows([]);
        setDbTotalRows(0);
        setDbTotalPages(1);
        setDbPage(1);
        setDbHasLoadedRows(false);
        setDbRowsError('');
        setDbSortBy('');
        setDbSortDir('desc');
    }, [dbSelectedTableKey, handleLoadDbColumns]);

    // Tables load when the tab is first opened.
    useEffect(() => {
        void handleLoadDbTables();
    }, [handleLoadDbTables]);

    const selectedDbTable = useMemo(
        () => dbTables.find((entry) => entry.key === dbSelectedTableKey) || null,
        [dbSelectedTableKey, dbTables],
    );
    const visibleDbColumnSet = useMemo(
        () => new Set(dbVisibleColumns),
        [dbVisibleColumns],
    );
    const visibleDbColumns = useMemo(
        () => dbColumns.filter((column) => visibleDbColumnSet.has(column.name)),
        [dbColumns, visibleDbColumnSet],
    );
    const handleDbSortColumn = useCallback((columnName) => {
        const nextSortDir = dbSortBy === columnName && dbSortDir === 'asc' ? 'desc' : 'asc';
        void handleLoadDbRows({
            page: 1,
            sortBy: columnName,
            sortDir: nextSortDir,
        });
    }, [dbSortBy, dbSortDir, handleLoadDbRows]);
    const handleDbApplyFilters = useCallback(() => {
        void handleLoadDbRows({ page: 1 });
    }, [handleLoadDbRows]);
    const handleDbToggleColumn = useCallback((columnName) => {
        setDbVisibleColumns((previous) => {
            if (previous.includes(columnName)) {
                return previous.filter((name) => name !== columnName);
            }
            return [...previous, columnName];
        });
    }, []);
    const hasPreviousDbPage = dbHasLoadedRows && dbPage > 1;
    const hasNextDbPage = dbHasLoadedRows && dbPage < dbTotalPages;

    return (
        <section className="admin-panel-card admin-db-explorer">
            <div className="admin-panel-card-header">
                <h3>DB Explorer</h3>
                <button
                    type="button"
                    className="admin-btn admin-btn--ghost"
                    onClick={handleLoadDbTables}
                    disabled={isDbTablesLoading}
                >
                    {isDbTablesLoading ? 'Loading...' : 'Load Tables'}
                </button>
            </div>
            <p className="admin-db-help">
                Tables load when you open this tab; rows are only fetched when you click Load Rows.
            </p>

            {dbTablesError && (
                <div className="admin-db-error">{dbTablesError}</div>
            )}

            {dbTables.length === 0 ? (
                <p className="admin-panel-empty">No tables loaded yet. Click "Load Tables".</p>
            ) : (
                <>
                    <div className="admin-db-controls-grid">
                        <label className="admin-db-field">
                            <span>Table</span>
                            <select
                                className="admin-db-select"
                                value={dbSelectedTableKey}
                                onChange={(event) => setDbSelectedTableKey(event.target.value)}
                            >
                                <option value="">Select table</option>
                                {dbTables.map((tableEntry) => (
                                    <option key={tableEntry.key} value={tableEntry.key}>
                                        {tableEntry.label}
                                        {Number.isFinite(tableEntry.rowCountEstimate)
                                            ? ` (~${formatInteger(tableEntry.rowCountEstimate)})`
                                            : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="admin-db-field">
                            <span>Filter column</span>
                            <select
                                className="admin-db-select"
                                value={dbFilterColumn}
                                onChange={(event) => setDbFilterColumn(event.target.value)}
                                disabled={!selectedDbTable || isDbColumnsLoading}
                            >
                                <option value="" disabled>
                                    {!selectedDbTable
                                        ? 'Select table first'
                                        : 'Loading columns...'}
                                </option>
                                {dbColumns.map((column) => (
                                    <option key={`filter-column-${column.name}`} value={column.name}>
                                        {column.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="admin-db-field">
                            <span>Filter value</span>
                            <input
                                type="text"
                                className="admin-db-input"
                                value={dbFilterValue}
                                onChange={(event) => setDbFilterValue(event.target.value)}
                                disabled={!dbFilterColumn}
                                placeholder={!dbFilterColumn ? 'Select filter column first' : 'Type filter value'}
                            />
                        </label>
                    </div>

                    {dbColumnsError && (
                        <div className="admin-db-error">
                            {dbColumnsError}
                        </div>
                    )}

                    <div className="admin-db-actions-row">
                        <button
                            type="button"
                            className="admin-btn"
                            onClick={handleDbApplyFilters}
                            disabled={!selectedDbTable || isDbRowsLoading}
                        >
                            {isDbRowsLoading ? 'Loading...' : (dbHasLoadedRows ? 'Apply Filters' : 'Load Rows')}
                        </button>
                        {dbHasLoadedRows && (
                            <span className="admin-db-page-meta">
                                Rows: {formatInteger(dbTotalRows)} | Page {formatInteger(dbPage)} / {formatInteger(dbTotalPages)}
                            </span>
                        )}
                    </div>

                    <div className="admin-db-columns">
                        <div className="admin-db-columns-head">
                            <h4>Columns</h4>
                        </div>
                        {dbColumns.length === 0 ? (
                            <p className="admin-panel-empty">No columns found.</p>
                        ) : (
                            <div className="admin-db-column-list">
                                {dbColumns.map((column) => (
                                    <label key={`column-toggle-${column.name}`} className="admin-db-column-chip">
                                        <input
                                            type="checkbox"
                                            checked={visibleDbColumnSet.has(column.name)}
                                            onChange={() => handleDbToggleColumn(column.name)}
                                        />
                                        <span className="admin-db-column-chip-name">{column.name}</span>
                                        {column.dataType && (
                                            <span className="admin-db-column-chip-type">{column.dataType}</span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {dbRowsError && (
                        <div className="admin-db-error">{dbRowsError}</div>
                    )}

                    {dbHasLoadedRows && (
                        <>
                            {dbRows.length === 0 ? (
                                <p className="admin-panel-empty">No rows found for this query.</p>
                            ) : visibleDbColumns.length === 0 ? (
                                <p className="admin-panel-empty">No visible columns selected.</p>
                            ) : (
                                <div className="admin-table-wrap">
                                    <table className="admin-usage-table admin-db-table">
                                        <thead>
                                            <tr>
                                                {visibleDbColumns.map((column) => {
                                                    const isActiveSort = dbSortBy === column.name;
                                                    const indicator = isActiveSort
                                                        ? (dbSortDir === 'asc' ? ' ▲' : ' ▼')
                                                        : '';
                                                    return (
                                                        <th key={`db-col-${column.name}`}>
                                                            <button
                                                                type="button"
                                                                className="admin-db-sort-btn"
                                                                onClick={() => handleDbSortColumn(column.name)}
                                                            >
                                                                {column.name}{indicator}
                                                            </button>
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dbRows.map((row, index) => (
                                                <tr key={`db-row-${index}`}>
                                                    {visibleDbColumns.map((column) => (
                                                        <td key={`db-cell-${index}-${column.name}`}>
                                                            <span className="admin-db-cell">
                                                                {formatCellValue(row[column.name])}
                                                            </span>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="admin-db-pagination">
                                <button
                                    type="button"
                                    className="admin-btn admin-btn--ghost"
                                    onClick={() => handleLoadDbRows({ page: dbPage - 1 })}
                                    disabled={!hasPreviousDbPage || isDbRowsLoading}
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    className="admin-btn admin-btn--ghost"
                                    onClick={() => handleLoadDbRows({ page: dbPage + 1 })}
                                    disabled={!hasNextDbPage || isDbRowsLoading}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </section>
    );
};

export default DatabaseTab;
